import { motion } from "framer-motion";
import Layout from "../components/layout/Layout";
import SEO, { articleSchema, breadcrumbSchema, orgSchema } from "../components/layout/SEO";
import Breadcrumbs from "../components/shared/Breadcrumbs";
import { Reveal } from "../components/shared/Reveal";
import CTAButton from "../components/shared/CTAButton";
import LangLink from "../components/shared/LangLink";
import { ArrowUpRight } from "lucide-react";
import { GUIDE_ROUTES } from "../data/guides";
import { SITE } from "../data/site";
import { useI18n } from "../i18n";

// Reusable guide/article template.
export default function GuideTemplate({ data }) {
  const { t, lang } = useI18n();
  const noindex = lang !== "en" || !!data.isPlaceholder;
  const related = (data.relatedGuides || [])
    .map((slug) => GUIDE_ROUTES.find((g) => g.slug === slug))
    .filter(Boolean);

  return (
    <Layout>
      <SEO
        title={`${data.title} | ${SITE.name}`}
        description={data.summary}
        path={`/${data.slug}`}
        noindex={noindex}
        type="article"
        schema={[
          orgSchema(),
          breadcrumbSchema([
            { name: "Home", to: "/" },
            { name: "Guides", to: "/guides" },
            { name: data.title, to: `/${data.slug}` },
          ]),
          articleSchema({ title: data.title, description: data.summary, image: data.image }),
        ]}
      />

      <Breadcrumbs items={[{ name: t("nav.guides"), to: "/guides" }, { name: data.title }]} />

      <article className="container-psv py-12 md:py-16">
        <Reveal className="max-w-4xl">
          <span className="eyebrow mb-4 block">{t("nav.guides")}</span>
          <h1 className="font-serif font-medium text-primary text-5xl md:text-6xl leading-[1.0] tracking-tight">{data.title}</h1>
          <p className="mt-6 text-xl text-secondary-foreground font-light leading-relaxed">{data.summary}</p>
        </Reveal>

        {data.image && (
          <Reveal delay={0.1} className="mt-12 relative overflow-hidden rounded-[2rem] grain h-[320px] md:h-[460px] border border-border">
            <motion.img src={data.image} alt={data.title} className="w-full h-full object-cover" loading="lazy" />
          </Reveal>
        )}

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-8">
            {data.sections.map((s, i) => (
              <Reveal key={i} className="mb-10">
                <h2 className="font-serif text-3xl md:text-4xl text-primary mb-4 tracking-tight">{s.h2}</h2>
                <p className="text-[17px] text-secondary-foreground leading-relaxed">{s.body}</p>
              </Reveal>
            ))}

            <Reveal className="mt-12 bg-surface border border-border rounded-lg p-8">
              <h3 className="font-serif text-2xl text-primary mb-3">Ready to understand your options?</h3>
              <p className="text-[15px] text-secondary-foreground mb-6">Request a private, no-obligation case review and receive guidance on the appropriate next steps.</p>
              <CTAButton to="/#case-review" testId="guide-cta-btn">{t("cta.caseReview")}</CTAButton>
            </Reveal>

            <p className="mt-8 text-[13px] text-muted-foreground leading-relaxed">
              <strong className="text-primary">Medical disclaimer:</strong> This article provides general information only and is not medical advice. Final recommendations come from a licensed medical professional.
            </p>
          </div>

          <aside className="lg:col-span-4">
            {related.length > 0 && (
              <div className="bg-surface border border-border rounded-lg p-7 lg:sticky lg:top-28">
                <h3 className="font-sans text-sm uppercase tracking-[0.14em] text-muted-foreground mb-4">Related guides</h3>
                <ul className="space-y-3">
                  {related.map((r) => (
                    <li key={r.slug}>
                      <LangLink to={`/${r.slug}`} className="group flex items-center justify-between gap-2 text-[15px] text-primary hover:text-accent transition-colors">
                        {r.title} <ArrowUpRight className="w-4 h-4 opacity-50 group-hover:opacity-100" />
                      </LangLink>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </article>
    </Layout>
  );
}
